import { API_URL } from '../common/constants';
import { IEnvelope, ISignatureSettings } from '../store/types';
import axiosWrapper from './AxiosWrapper';
import { AxiosInstance } from 'axios';

// import termsEnvelope from '../resources/mock/termsEnvelope.json';
// import interactiveEnvelope from '../resources/mock/interactiveEnvelope.json';
// import wireFraudEnvelopeBySignerId from '../resources/mock/wireFraudEnvelopeBySignerId.json';

export interface IResponse<T> {
  result: T;
  success: boolean;
  statusCode: number;
  errors: any[];
}

export interface IEnvelopeSubmitResult {
  executedDocuments: [
    {
      documentId: string;
      url: string;
    }
  ];
  envelopeId: string;
  signer: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface IGetOptions {
  envelopeId: string;
  signerId: string;
}

interface IPostOptions {
  envelopeId: string;
  signerId: string;
  data: any;
}

interface IDisclosureOptions {
  envelopeId: string;
  signerId: string;
}

interface IEnvelopeDownloadResponse<T> {
  data: any;
  status: number;
}

interface IDownloadEnvelopeOptions {
  envelopeId: string;
  signerId: string;
  fileType: string;
}

export default class Server {
  private api: AxiosInstance;

  public constructor(apiKey: string, accessToken: string) {
    this.api = axiosWrapper.init({ apiKey, accessToken });
  }

  public async getEnvelopeById(options: IGetOptions): Promise<IResponse<IEnvelope>> {
    const { envelopeId, signerId } = options;
    const res = await this.api.get(`${API_URL}/envelopes/${envelopeId}/signer/${signerId}`);
    let { result, success, statusCode, errors } = res.data;
    result.serverTime = res.headers['x-date-time'];
    return {
      result,
      success,
      statusCode,
      errors,
    };
  }

  public async submitEnvelope(options: IPostOptions): Promise<IResponse<IEnvelopeSubmitResult>> {
    const { envelopeId, signerId, data } = options;
    const res = await this.api.post(`${API_URL}/envelopes/${envelopeId}/stamp?signerId=${signerId}`, data);
    let { result, success, statusCode, errors } = res.data;
    return {
      result,
      success,
      statusCode,
      errors,
    };
  }

  public async submitEsignDisclosureAgreement(options: IDisclosureOptions): Promise<IResponse<null>> {
    const { envelopeId, signerId } = options;
    const res = await this.api.patch(`${API_URL}/envelopes/${envelopeId}/signer/${signerId}/agree`);
    let { result, success, statusCode, errors } = res.data;
    return {
      result,
      success,
      statusCode,
      errors,
    };
  }

  public async submitSignatureAgreement(
    envelopeId: string,
    signerId: string,
    signatureSettings: ISignatureSettings,
    useExistingSettings: boolean
  ): Promise<IResponse<null>> {
    const data: any = {
      useExistingSettings,
    };
    // If we are using existing settings, we don't need to send the signature settings
    if (!useExistingSettings) {
      if (signatureSettings.signatureType === 'text') {
        data.signingSettings = {
          signatureFont: signatureSettings?.signatureFont,
          signatureText: signatureSettings?.signatureValue,
          initialsText: signatureSettings?.initialsValue,
        }
      } else {
        data.signingSettings = {
          signatureImageBase64: signatureSettings?.signatureValue.split(',')[1],
          initialsImageBase64: signatureSettings?.initialsValue.split(',')[1],
        }
      }
      if (signatureSettings.saveSigningSettings) data.saveSigningSettings = signatureSettings.saveSigningSettings;
    }
    const res = await this.api.put(
      `${API_URL}/envelopes/${envelopeId}/signers/${signerId}/signatureSettings?api-version=2.0`,
      data
    );
    let { result, success, statusCode, errors } = res.data;
    return {
      result,
      success,
      statusCode,
      errors,
    };
  }

  public async getPdfPreSignedUrl({
    envelopeId,
    documentId,
  }: {
    envelopeId: string;
    documentId: string;
  }): Promise<string> {
    const response = await this.api.get(`${API_URL}/envelopes/${envelopeId}/documents/${documentId}/downloadURL`);
    return response.data.result.preSignedDownloadUrl;
  }

  public async getThumbnailsByEnvelope(options: any): Promise<IResponse<null>> {
    const { envelopeId } = options;
    const res = await this.api.get(`${API_URL}/thumbnails/envelopes/${envelopeId}?expand=document.thumbnail&width=75`);
    const { result, success, statusCode, errors } = res.data;
    return {
      result,
      success,
      statusCode,
      errors,
    };
  }

  public async downloadEnvelope(options: IDownloadEnvelopeOptions): Promise<IEnvelopeDownloadResponse<null>> {
    const { envelopeId, signerId, fileType } = options;
    const response = await this.api.get(
      fileType === 'zip'
        ? `${API_URL}/envelopes/${envelopeId}/zip?signerId=${signerId}`
        : `${API_URL}/envelopes/${envelopeId}/pdf?api-version=2.0`,
      {
        responseType: 'blob',
      }
    );
    const { data, status } = response;
    return {
      data,
      status,
    };
  }
}
